import React from 'react'
import { Stack } from '@hub/stack/src'
import { StoreHeader } from '@scentregroup/shared/hub-components/store-header'
import { StorefrontUtility } from '@scentregroup/shared/hub-components/storefront-utility'
import { mapStoreTradingHourDayToTradingSchedule } from '@scentregroup/shared/helpers/get-trading-schedule'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import { Template } from '@scentregroup/shared/components/page-templates/template'
import RetailerStoresTileList from '@scentregroup/shared/hub-components/tile-list/retailer-stores-tile-list'
import { ContentDetail } from '../../../lib/get-content-detail'
import { SGPageTemplate } from '@scentregroup/shared/types/page-templates'
import { Store as CentreStore } from '@scentregroup/shared/types/posts'
import { SimpleStore } from '../../../lib/get-content-detail/cinema'

type SimpleStoreWithRetailerTitle = SimpleStore & {
  retailer: SimpleStore['retailer'] & {
    title: string
  }
}

const isSimpleStoreWithRetailerTitle = (
  store: Store
): store is SimpleStoreWithRetailerTitle => {
  return typeof (store.retailer as any).title === 'string'
}

type Store = ContentDetail['store'] | SimpleStore | SimpleStoreWithRetailerTitle

type RetailerStore = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<Store['retailer']['linkedFrom']>['storeCollection']
    >['items']
  >[number]
>

interface IStoreContentDetailProps {
  centre: React.ComponentProps<typeof StorefrontUtility>['centre']
  content: Store
  template: SGPageTemplate
  storeTradingHours?: TradingHourDay[]
  centreTradingHours?: TradingHourDay[]
}

function getNationalStorefrontUrl(store: Store): string | undefined {
  const { sys, slug, linkedFrom } = store.retailer
  const hasNationalStorefront =
    slug && linkedFrom?.nationalStorefrontCollection?.items?.length
  return hasNationalStorefront ? `/store/${sys.id}/${slug}` : undefined
}

export const StoreContentDetail: React.FC<
  React.PropsWithChildren<IStoreContentDetailProps>
> = ({
  centre,
  content: store,
  template,
  storeTradingHours,
  centreTradingHours,
  children,
}) => {
  const subTitle = ''
  const scrollToId = ''
  const longDescription = inherit(store, 'longDescription')
  const heroImageSet =
    inherit(store, 'heroImageSet') ??
    [
      ...(store.retailCategoriesCollection?.items ?? []),
      ...(store.retailer.retailCategoriesCollection?.items ?? []),
    ].find(rc => rc?.heroImageSet)?.heroImageSet

  const retailerStores =
    store.retailer?.linkedFrom?.storeCollection?.items || []

  const isRetailerStore = (
    store: RetailerStore | null
  ): store is RetailerStore => store !== null
  const nonNullStores = retailerStores.filter(isRetailerStore)
  const stateFilteredStores = nonNullStores.filter(
    (item: RetailerStore) => item?.centre?.state === centre?.state
  )

  const transformedStores = stateFilteredStores.map((store: RetailerStore) => ({
    id: store.sys.id,
    slug: store.slug,
    centre: {
      slug: store.centre?.slug ?? null,
      title: store.centre?.title ?? null,
      state: store.centre?.state ?? null,
    },
    physicalAddress: store.physicalAddress,
    landmarkDescription: store.landmarkDescription,
    legacyIdentifier: store.legacyIdentifier,
  }))
  type ExtraStoreProperties = {
    centre: { state: string | null }
    legacyIdentifier: number | null
  }
  const validStores = transformedStores.filter(
    (store): store is CentreStore & ExtraStoreProperties =>
      store.slug !== null &&
      store.centre.title !== null &&
      store.centre.slug !== null
  )

  const storeTileRetailer = isSimpleStoreWithRetailerTitle(store) && {
    id: store.retailer.sys.id,
    title: store.retailer.title,
    slug: store.slug,
  }

  return (
    <Stack gap={['spacing-3xl', null, 'spacing-4xl']}>
      <StoreHeader
        title={store.title}
        retailerLogo={store.retailer.retailerLogoSet?.coloured?.url}
        subTitle={subTitle}
        longDescription={longDescription}
        heroImage={heroImageSet?.master?.url}
        scrollToId={scrollToId}
        nationalStorefrontUrl={getNationalStorefrontUrl(store)}
        enableH1Heading={false}
      />
      <StorefrontUtility
        centre={centre}
        store={store}
        centreTradingSchedule={mapStoreTradingHourDayToTradingSchedule(
          centreTradingHours || []
        )}
        storeTradingSchedule={mapStoreTradingHourDayToTradingSchedule(
          storeTradingHours || []
        )}
        enableH1Heading={true}
      />
      {children}
      <Template id={scrollToId} template={template} activateH1Title={false} />
      {validStores.length > 1 && storeTileRetailer ? (
        <RetailerStoresTileList
          retailer={storeTileRetailer}
          stores={validStores}
          state={centre.state}
        />
      ) : null}
    </Stack>
  )
}

type HeritableKeys = keyof ContentDetail['store'] &
  keyof ContentDetail['store']['retailer'] &
  keyof SimpleStore &
  keyof SimpleStore['retailer']

function inherit<K extends HeritableKeys>(
  store: ContentDetail['store'] | SimpleStore,
  key: K
):
  | ContentDetail['store'][K]
  | SimpleStore[K]
  | ContentDetail['store']['retailer'][K]
  | SimpleStore['retailer'][K] {
  if (store[key]) {
    return store[key]
  }
  return store.retailer[key]
}
